import React, { useState, useMemo, useLayoutEffect } from 'react';
import { Box, Typography, Divider, Button, IconButton, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/layout';
import { useForm, FormProvider } from 'react-hook-form';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';


export const conditionsOptions = [
  { value: "==", label: "common.conditionsOption.equalTo"},
  { value: "!=", label: "common.conditionsOption.notEqualTo"},
  { value: ">",  label: "common.conditionsOption.greaterThan"},
  { value: ">=", label: "common.conditionsOption.greaterThanOrEqualTo"},
  { value: "<",  label: "common.conditionsOption.lessThan"},
  { value: "<=", label: "common.conditionsOption.lessThanOrEqualTo"}
];

export const dataTypeOptions = [
  { value: "string", label: "String" },
  { value: "int", label: "Integer" },
  { value: "bool", label: "Boolean" }
];

export const policies = {
  action: {
    name: 'action',
    input: 'select',
    label: 'workflowEdit.components.task.form.add.action',
  }
}

export function createConditionFields(key, defaultValues={}) {
  const {
    field=`condition-${key+1}`,
    condition=conditionsOptions[0].value,
    dataType=dataTypeOptions[0].value,
    value="empty"
  } = defaultValues;

  return [
    {
      name: `${key}.field`,
      input: 'textField',
      label: 'workflowEdit.components.task.form.connection.field.label',
      placeholder: 'workflowEdit.components.task.form.connection.field.placeholder',
      rules: {
        required: { value: true },
      },
      defaultValue: field
    },
    {
      name: `${key}.condition`,
      input: 'select',
      label: 'workflowEdit.components.task.form.connection.condition',
      options: conditionsOptions,
      defaultValue: condition
    },
    {
      name: `${key}.dataType`,
      input: 'select',
      label: 'workflowEdit.components.task.form.connection.dataType',
      options: dataTypeOptions,
      defaultValue: dataType
    },
    {
      name: `${key}.value`,
      input: 'textField',
      label: 'workflowEdit.components.task.form.connection.result.label',
      placeholder: 'workflowEdit.components.task.form.connection.result.placeholder',
      rules: {
        required: { value: true },
      },
      defaultValue: value
    }
  ];
}

export function ConditionComponent({ index, fieldsValues={}, setValue, t, handleRemoveCondition }) {
  const [expanded, setExpanded] = useState(true);
  const fields = createConditionFields(index);
  const {
    field=`condition-${index+1}`,
    condition=conditionsOptions[0].value,
    dataType=dataTypeOptions[0].value,
    value="empty"
  } = fieldsValues;

  setValue(`${index}`, { field, condition, dataType, value });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '.3s' }}
        >
          <ArrowDropDownOutlinedIcon />
        </IconButton>
        <Typography variant="overline">{t('workflowEdit.components.task.form.connection.conditionSelected', { number: index + 1 })}</Typography>

        <IconButton
          onClick={(e) => handleRemoveCondition(e, index)}
          size="small"
        >
          <DeleteForeverRoundedIcon />
        </IconButton>

      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          { fields.map((field, i) => (
            <Input
              {...field}
              key={`${field.name}-${i}`}
              sx={{ width: '100%' }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ConnectionComponent({ task , optionsAction, handleCancel, handleConnection, handleDelete }) {
  const { job, con } = task;

  const [t] = useTranslation();


  const methods = useForm({
    defaultValues: {
      action: con?.conx || 'succed',
    }
  });
  const { handleSubmit, watch, setValue, unregister } = methods;
  const watchAction = watch('action');
  const watchConditions = watch('conditions');
  const [conditionFields, setConditionFields] = useState([]);

  function handleAddCondition(e) {
    e.preventDefault();
    setConditionFields([
      ...conditionFields,
      {
        field: 'condition',
        condition: conditionsOptions[0].value,
        dataType: dataTypeOptions[0].value,
        value: 'empty'
      }
    ]);
  }

  function handleOnSubmit(data) {
    const { action, ...fields } = data;
    job.conn[con.index].conx = action;

    if (action === "condition") job.conn[con.index]['conditions'] = Object.values(fields);

    handleConnection(job);
  }

  function handleRemoveCondition(e, indexToRemove) {
    e.preventDefault();
    setConditionFields(
      conditionFields.filter((item, index) => index !== indexToRemove)
    );
    unregister(`${indexToRemove}`);
  }

  useLayoutEffect(()=> {
  if (con && !!con.conditions && con.conditions.length > 0) {
    setConditionFields(con.conditions);
  }
}, [])

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
      overflowY: 'auto' }}
    >
      <FormProvider { ...methods }>
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Box sx={{ display: 'flex', p: 2, flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('workflowEdit.components.task.connectionLabel')}</Typography>
            <Divider/>
            <Input
              {...policies.action}
              options={optionsAction}
              sx={{ width: '100%' }}
            />
            <Divider/>

            { watchAction === 'condition' &&
              <>
                { conditionFields.map((field, index) => (
                  <ConditionComponent key={index} index={index} fieldsValues={field} setValue={setValue} handleRemoveCondition={handleRemoveCondition} t={t}/>
                ))}
                <Button variant="text" sx={{ width: '100%' }} onClick={handleAddCondition}>
                  {t('workflowEdit.components.task.form.condition')}
                </Button>
                <Divider/>
              </>
            }

            <Button variant="outlined" sx={{ width: '100%' }} type='submit'>
              {t('workflowEdit.components.task.form.save')}
            </Button>
          </Box>
        </form>
      </FormProvider>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 4, justifyContent: 'flex-end' }}>
        <Button variant="text" sx={{ width: '100%' }} onClick={() => handleDelete(task)} color="error">
          {t('workflowEdit.components.task.form.delete')}
        </Button>
        <Button variant="text" sx={{ width: '100%' }} onClick={handleCancel}>
          {t('workflowEdit.components.task.form.cancel')}
        </Button>
      </Box>
    </Box>
  );
}
